<script>
import { mapGetters } from 'vuex'
import ChargeResource from '@/resources/charge'
import PaymentMethod from '@/components/PaymentMethod.vue'
import CostBreakdown from '@/components/CostBreakdown.vue'
import UserEditForm from '@/components/UserEditForm.vue'
import VoucherPitch from '@/components/VoucherPitch.vue'
import TermsOfServiceLink from '@/components/TermsOfServiceLink.vue'
import WaiverModal from '@/components/WaiverModal.vue'
import UserUpcomingReservations from '../components/UserUpcomingReservations.vue'
import ReservationRacersForm from '../components/ReservationRacersForm.vue'
import ReservationSeatingGroupResource from '@/resources/reservationSeatingGroup'
import messages from '@/mixins/messages'

export default {
    name: 'Dashboard',
    mixins: [messages],
    components: {
        PaymentMethod,
        CostBreakdown,
        UserEditForm,
        VoucherPitch,
        TermsOfServiceLink,
        WaiverModal,
        UserUpcomingReservations,
        ReservationRacersForm,
    },
    computed: {
        ...mapGetters(['userResource', 'isApexClub', 'creditsBalance']),
        waiversOutstandingIds() {
            return this.$refs.waiverModal.waiversOutstanding.map((waiver) => waiver.id)
        },
        waiverApprovalsIds() {
            return this.$refs.waiverModal.waiverApprovals.map((approval) => approval.id)
        },
    },
    data() {
        return {
            loading: true,
            showPaymentMethod: true,
            chargeResources: [],
            currentTabIndex: 0,
            lapsPage: null,
            lapsNoneReorded: false,
            lapTimeQuery: '',
            lapsLoading: false,
            calendarResource: null,
            waiverResources: [],
            waiverViewing: null,
            optInChangesMade: false,
            optInAccount: null,
            optInMarketing: null,
            upcomingReservationsData: null,
            myRacerGroups: [],
            editingGroup: null,
            savingRacers: false,
        }
    },
    methods: {
        async load() {
            this.loading = true
            this.chargeResources = await ChargeResource.all({ query: { amount_cents__gt: 0 } })
            ;(this.optInAccount = this.userResource.attributes.opt_in_account_updates), (this.optInMarketing = this.userResource.attributes.opt_in_marketing_updates)
            this.loading = false

            this.$refs.waiverModal.$on('waivers', (waivers) => (this.waiverResources = waivers))
            this.loadRacerGroups()
        },
        async loadRacerGroups() {
            try {
                const groups = await ReservationSeatingGroupResource.mine()
                this.myRacerGroups = groups.filter((group) => String(group.attributes.created_by) === String(this.userResource.id))
            } catch (e) {
                this.myRacerGroups = []
            }
        },
        groupReservationIds(group) {
            return group.seats().map((seat) => seat.reservation)
        },
        openEditRacers(group) {
            this.editingGroup = group
            this.$refs.editRacersModal.show()
        },
        async saveEditRacers() {
            this.savingRacers = true
            const ok = this.$refs.editRacersForm ? await this.$refs.editRacersForm.run() : true
            this.savingRacers = false
            if (ok) {
                this.$refs.editRacersModal.hide()
                this.editingGroup = null
                await this.loadRacerGroups()
            }
        },
        async onUnenrollSubscription() {
            this.$refs.cancelSubscription.hide()
            await ChargeResource.creditUnenrollUser()
            this.userResource.set('credits_subscription_in_good_standing', false)
        },
        async onSaveNotifPrefs() {
            this.userResource.attributes.opt_in_account_updates = this.optInAccount
            this.userResource.attributes.opt_in_marketing_updates = this.optInMarketing
            await this.userResource.save()
            this.optInChangesMade = false
        },
        nthDayOfMonth(day) {
            const moment = require('moment')
            return moment(day, 'D').format('Do')
        },
    },
    async mounted() {
        await this.load()
    },
}
</script>

<template>
    <div>
        <header-image src="hero-main.png">
            <h1>Dashboard</h1>
        </header-image>

        <b-container class="mt-3 mb-5" v-if="!loading">
            <b-row>
                <b-col>
                    <user-upcoming-reservations poll-seconds="5" @reservations="(reservationData) => (upcomingReservationsData = reservationData)" />

                    <div v-if="$appConfigBool('APEX_CLUB_ENABLED', true) && !isApexClub">
                        <b-alert variant="info" class="mb-0" show
                            ><strong>Save 20% on every race</strong> by joining <strong>{{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</strong
                            >. (And a whole lot more!) <a href="javascript:{}" @click="$router.push({ name: 'subscription' })">More Info</a></b-alert
                        >
                    </div>

                    <b-card class="mb-3 mt-3" no-body>
                        <b-tabs class="scrollable-tabs-header" card>
                            <b-tab title="Profile">
                                <b-row>
                                    <b-col offset-md="2" md="8">
                                        <user-edit-form :user-resource="userResource" submit-text="Save Profile" success-message="Profile Saved!" />
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <b-tab title="Payment">
                                <b-row>
                                    <b-col offset-md="3" md="6">
                                        <b-card>
                                            <payment-method cols-lg="12" :userResource="userResource" />
                                        </b-card>
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <b-tab title="Subscription" v-if="$appConfigBool('APEX_CLUB_ENABLED', true) && isApexClub">
                                <dl v-if="userResource.attributes.credits_subscription_in_good_standing" class="row mb-0">
                                    <dt class="col-sm-4">Apex Club</dt>
                                    <dd class="col-sm-8">
                                        <strong class="text-success"><b-icon icon="patch-check-fill" /> Member</strong>
                                    </dd>
                                    <dt class="col-sm-4">Credits Balance</dt>
                                    <dd class="col-sm-8">
                                        <strong><b-icon icon="currency-exchange" /> {{ creditsBalance }} Credits</strong>
                                    </dd>
                                    <dt class="col-sm-4">Subscription</dt>
                                    <dd class="col-sm-8">
                                        <strong
                                            ><b-icon icon="arrow-repeat" /> {{ userResource.attributes.credits_recurring }} credits monthly on the {{ nthDayOfMonth(userResource.attributes.credits_cycle_day_of_month) }} — {{ currencySymbol()
                                            }}{{ moneyFormat(userResource.attributes.credits_recurring_cents) }}</strong
                                        >
                                    </dd>
                                    <dt class="col-sm-4">&nbsp;</dt>
                                    <dd class="col-sm-8">
                                        <small><a href="javascript:{};" class="text-muted" @click="$refs.cancelSubscription.show()">Cancel Subscription</a></small>
                                    </dd>
                                </dl>

                                <div v-else-if="!userResource.attributes.credits_subscription_in_good_standing && userResource.attributes.credits_subscription_exists">
                                    <b-alert variant="danger" class="mb-3" show
                                        ><strong>Uh-oh!</strong> You have an active {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }} membership but your account is not in good standing. Please <a href="javascript:{}" @click="currentTabIndex = 1">review your Payment Method</a> to fix this.</b-alert
                                    >
                                </div>

                                <div v-else>
                                    <b-alert variant="info" class="mb-0" show
                                        ><strong>Save 20% on every race</strong> by joining <strong>{{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}</strong
                                        >. (And a whole lot more!) <a href="javascript:{}" @click="$router.push({ name: 'subscription' })">More Info</a></b-alert
                                    >
                                </div>
                            </b-tab>

                            <b-tab title="Notifications">
                                <b-row>
                                    <b-col offset-md="2" md="8">
                                        <b-card>
                                            <small>
                                                <b-form-checkbox v-model="optInAccount" @input="optInChangesMade = true" switch>
                                                    By checking this box, I consent to receive transactional messages related to my account, orders, or services I have requested. These messages may include appointment reminders, order confirmations, and account notifications among others. Message
                                                    frequency may vary. Message & Data rates may apply. Reply HELP for help or STOP to opt-out.
                                                </b-form-checkbox>
                                                <b-form-checkbox v-model="optInMarketing" @input="optInChangesMade = true" class="mt-3" switch>
                                                    By checking this box, I consent to receive marketing and promotional messages, including special offers, discounts, new product updates among others. Message frequency may vary. Message & Data rates may apply. Reply HELP for help or STOP to
                                                    opt-out.
                                                </b-form-checkbox>
                                            </small>
                                            <b-button variant="primary" :disabled="!optInChangesMade" @click="onSaveNotifPrefs()" class="mt-3">Save Notifications</b-button>
                                        </b-card>
                                    </b-col>
                                </b-row>
                            </b-tab>

                            <b-tab title="Upcoming Reservations">
                                <div v-if="upcomingReservationsData && upcomingReservationsData.future.length > 0">
                                    <b-table-simple class="nowrap-table" responsive>
                                        <b-thead>
                                            <b-th>Date</b-th>
                                            <b-th>Simulator</b-th>
                                            <b-th>&nbsp;</b-th>
                                            <b-th>&nbsp;</b-th>
                                        </b-thead>
                                        <b-tbody>
                                            <b-tr v-for="(reserv, i) in upcomingReservationsData.future" :key="i">
                                                <b-td>{{ [reserv.getDateFormatted(), reserv.getTimeFormatted()].join(' @ ') }}</b-td>
                                                <b-td>{{ reserv.attributes.calendar_name }}</b-td>
                                                <b-td
                                                    ><a :href="reserv.getAddToCalendarLink()"><b-icon icon="calendar-plus" /> Add to Calendar</a></b-td
                                                >
                                                <b-td
                                                    ><b-link :to="{ name: 'calendarControlWaitingRoom', params: { calendarId: reserv.attributes.calendar } }"><b-icon icon="controller" /> Go to Sim Controller</b-link></b-td
                                                >
                                            </b-tr>
                                        </b-tbody>
                                    </b-table-simple>
                                </div>

                                <div v-else class="text-center pt-5 pb-5">
                                    <h3><b-icon icon="cone-striped" style="color: #ed8d34" /></h3>
                                    <p>You don't have any upcoming reservations</p>
                                    <b-button variant="secondary" :to="{ name: 'reservations' }">View Availability</b-button>
                                </div>
                            </b-tab>

                            <b-tab title="Your Racers" v-if="myRacerGroups.length > 0">
                                <div v-for="group in myRacerGroups" :key="group.id" class="mb-4">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <strong>{{ group.attributes.created ? new Date(group.attributes.created).toLocaleDateString() : 'Booking' }} &mdash; {{ group.seats().length }} simulators</strong>
                                        <b-button size="sm" variant="secondary" @click="openEditRacers(group)">Add / edit racers</b-button>
                                    </div>
                                    <b-list-group>
                                        <b-list-group-item v-for="seat in group.seats()" :key="seat.reservation" class="d-flex justify-content-between">
                                            <span
                                                >{{ seat.calendar_name }} <span class="text-muted small">{{ seat.time ? new Date(seat.time).toLocaleString() : '' }}</span></span
                                            >
                                            <span>{{ seat.seated_user_display_name || 'Open' }}</span>
                                        </b-list-group-item>
                                    </b-list-group>
                                </div>
                            </b-tab>

                            <b-tab title="Waivers">
                                <b-table-simple class="nowrap-table" responsive v-if="waiverResources.length > 0">
                                    <b-thead>
                                        <b-th>&nbsp;</b-th>
                                        <b-th>Status</b-th>
                                        <b-th>Waiver Name</b-th>
                                    </b-thead>
                                    <b-tbody>
                                        <b-tr v-for="waiver in waiverResources" :key="waiver.id">
                                            <b-td class="text-right">
                                                <b-button variant="danger" size="sm" v-if="waiversOutstandingIds.includes(waiver.id)" @click="waiverViewing = waiver">Sign</b-button>
                                                <b-button variant="outline-primary" size="sm" v-else @click="waiverViewing = waiver">View</b-button>
                                            </b-td>
                                            <b-td>{{ waiver.attributes.required ? 'Required' : 'Optional' }}</b-td>
                                            <b-td :class="{ 'font-weight-bold': waiver.attributes.required }">{{ waiver }}</b-td>
                                        </b-tr>
                                    </b-tbody>
                                </b-table-simple>

                                <p v-else class="text-center">No waivers to sign.</p>
                            </b-tab>

                            <b-tab title="Lap Times" class="text-center">
                                <b-button-group>
                                    <b-button variant="outline-primary" :to="{ name: 'laps' }"><b-icon icon="stopwatch-fill" /> Go to MyLaps</b-button>
                                    <b-button variant="outline-primary" :to="{ name: 'leaderboards' }"><b-icon icon="table" /> Go to Leaderboards</b-button>
                                </b-button-group>
                            </b-tab>

                            <b-tab title="Race Presets" class="text-center">
                                <p>Get race-ready your way. <strong>Customize your car, track, assists, and session settings</strong> before you hit the grid. Whether you want a casual cruise or a competitive challenge, fine-tune your experience anytime before the race begins.</p>
                                <b-button variant="outline-primary" :to="{ name: 'racesPresets' }"><b-icon icon="collection-play" /> Customize your Race</b-button>
                            </b-tab>

                            <b-tab title="Recent Transactions">
                                <b-table-simple v-if="chargeResources.length > 0" class="nowrap-table mt-2" responsive>
                                    <b-thead>
                                        <b-th>Amount</b-th>
                                        <b-th>Description</b-th>
                                        <b-th>Receipt</b-th>
                                    </b-thead>
                                    <b-tbody>
                                        <b-tr v-for="(chargeResource, i) in chargeResources" :key="i">
                                            <b-td>{{ currencySymbol() }}{{ chargeResource.amount() }}</b-td>
                                            <b-td
                                                >{{ chargeResource.get('notes') || 'No Description' }}<br /><small class="text-muted"
                                                    >{{ chargeResource.createdTimePretty() }} <span class="text-danger" v-if="chargeResource.get('errors')"><br />{{ chargeResource.get('errors') }}</span></small
                                                ></b-td
                                            >
                                            <b-td>
                                                <a :href="chargeResource.get('receipt_url')" target="_blank" v-if="chargeResource.attributes.receipt_url">View</a>
                                                <span v-else class="text-muted">&nbsp;</span>
                                            </b-td>
                                        </b-tr>
                                    </b-tbody>
                                </b-table-simple>
                                <p v-else class="text-center">No Recent Transactions</p>
                            </b-tab>
                        </b-tabs>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>

        <b-modal
            title="Cancel Subscription"
            ref="cancelSubscription"
            @ok="onUnenrollSubscription()"
            v-if="userResource"
            :ok-disabled="!userResource.attributes.credits_subscription_cancelable"
            cancel-title="Don't Cancel"
            cancel-variant="primary"
            ok-title="Cancel"
            ok-variant="danger"
            centered
            no-fade
        >
            <p>
                Are you sure you want to cancel your subscription? This action is irreversible.
                <span v-if="creditsBalance > 0"
                    ><strong>You will lose your balance of {{ creditsBalance }} Credits!</strong> Consider using your credits to book a reservation before canceling.</span
                >
            </p>
            <b-alert variant="danger" :show="!userResource.attributes.credits_subscription_cancelable">
                <strong>Sorry! You can't cancel your subscription yet</strong> because you are required to be a member for at least two billing cycles. Please be sure to view our <terms-of-service-link text="Terms of Service" el="span" />.
            </b-alert>
        </b-modal>

        <waiver-modal ref="waiverModal" v-model="waiverViewing" />

        <b-modal ref="editRacersModal" title="Your Racers" no-fade no-enforce-focus centered @ok.prevent="saveEditRacers" ok-title="Save" cancel-title="Close" :ok-disabled="savingRacers">
            <reservation-racers-form v-if="editingGroup" ref="editRacersForm" :reservation-ids="groupReservationIds(editingGroup)" :purchaser="userResource" :existing-group="editingGroup" />
        </b-modal>
    </div>
</template>

<style lang="scss">
.add-card {
    padding-top: 10px;
}

.b-icon.gold {
    color: #ecbd00;
}

.lap-times-table {
    overflow: scroll;

    table {
        min-width: 900px;
    }
}

.tabs.scrollable-tabs-header {
    .card-header {
        overflow-x: scroll;
        overflow-y: hidden;
        height: 55px;

        ul.card-header-tabs {
            width: 1200px;
        }
    }
}

.nowrap-table {
    td,
    th {
        white-space: nowrap;
    }
}
</style>
